import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Form, Input, Select, Switch } from "antd";
import PhoneInput from "react-phone-input-2";
import { useSelector, useDispatch } from "react-redux";
import _find from "lodash/find";
import _flattenDeep from "lodash/flatMapDeep";
import _ from "lodash";

import { selectAllDrivers } from "../../../drivers/store/slice";
import { getDriverList } from "modules/admin/drivers/store/thunks";
import { fullName } from "helper/util";
import { MARKET_OPTIONS, INDUSTRY_OPTIONS } from "configs/constants";
import DefaultMapbox from "modules/client/active-orders/components/mapbox/DefaultMapbox";
import AddressInput from "components/inputs/AddressInput";
import { City, State } from "country-state-city";
import { PUERTO_RICO_STATES_AND_CITIES } from "modules/shared/pr-states-cities";
import { ArrowDownIcon } from "components/svgs";
import { selectAllServices } from "modules/admin/settings/store/ServiceSlice";
import { getServiceList } from "modules/admin/settings/store/ServiceThunks";
import { loadAllCountries } from "helper/util";
import LogoUpload from "components/LogoUpload/LogoUpload";
import adminClientApi from "../../api/adminClientApi";

const { Option } = Select;
const defaultOptionName = "Please Select";
const isoCodePR = "PR";

const CompanyInfoTab = forwardRef(({ client, onChange }, ref) => {
  let driversList = useSelector(selectAllDrivers);
  let servicesList = useSelector(selectAllServices);
  const [countryStates, setCountryStates] = useState([]);
  const [stateCities, setStateCities] = useState([]);
  const [currentAddress, setCurrentAddress] = useState();
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // Logo upload state
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const servicesOptions = servicesList
    .filter((item) => item.name && !item.isDefault)
    ?.map((service) => ({
      label: service.name,
      value: service.id
    }));

  if (client) {
    driversList = [...driversList, ...client.drivers];
    servicesList = [...servicesList, client.services];
  }

  useEffect(() => {
    dispatch(getDriverList({ pageSize: 1000 }));
    dispatch(getServiceList());

    if (client) {
      const driverIds = client.drivers.map((driver) => driver.id);
      const serviceIds = client.services?.map((service) => service.id);
      const dataForm = {
        ...client,
        driverIds,
        services: serviceIds,
        address: client.address, // Ensure address is explicitly set
        customID: client.customID, // Ensure customID is explicitly set
        defaultRouteVisibility: client.defaultRouteVisibility || "all" // Default to "all" if not set
      };
      setCurrentAddress({
        address: client.address,
        lngLat: [client.lng, client.lat]
      });
      form.setFieldsValue(dataForm);

      // Set logo URL if client has one
      if (client.logoPhoto?.url) {
        setLogoUrl(client.logoPhoto.url);
      }
    }
  }, [client, form, dispatch]);

  const onChangeCountry = async (isoCodeCountry) => {
    if (!isoCodeCountry) return;
    form.setFieldsValue({ state: defaultOptionName });
    form.setFieldsValue({ city: defaultOptionName });
    let stateList = [];
    if (isoCodeCountry === isoCodePR) {
      stateList = PUERTO_RICO_STATES_AND_CITIES.map((item, idx) => ({
        name: item.state,
        isoCode: `${isoCodePR}-${idx}`
      }));
    } else {
      stateList = await State.getStatesOfCountry(isoCodeCountry);
    }
    setCountryStates(stateList);
  };

  const onChangeState = (stateName) => {
    const isoCodeState = _find(countryStates, { name: stateName })?.isoCode;
    if (!isoCodeState) return;
    form.setFieldsValue({ city: defaultOptionName });
    const isoCodeCountry = form.getFieldValue("country");
    let cities = [];
    if (isoCodeCountry === isoCodePR) {
      cities = _flattenDeep(
        PUERTO_RICO_STATES_AND_CITIES.filter((item) => item.state === stateName).map(
          (filteredItem) => [...filteredItem.cities]
        )
      ).map((city) => ({ name: city }));
    } else {
      cities = City.getCitiesOfState(isoCodeCountry, isoCodeState);
    }
    setStateCities(cities);
  };

  const handleSetAddress = (value) => {
    setCurrentAddress({ address: value.address, lngLat: value.lngLat });
    // Update the form field value to prevent validation errors
    form.setFieldsValue({ address: value.address });
    // Trigger change detection for save button
    if (onChange) onChange();
  };

  const filterDriver = (name) => {
    if (!name) return;
    dispatch(getDriverList({ filter: [`name||$cont||${name}`] }));
  };

  const debouncedFilterDriver = _.debounce((input) => {
    filterDriver(input);
  }, 1000);

  const handleFieldChange = () => {
    if (onChange) onChange();
  };

  const handleLogoUpload = async (formData) => {
    if (!client?.id) {
      throw new Error("Client ID not available");
    }

    setUploadingLogo(true);
    try {
      const response = await adminClientApi.uploadClientLogo(client.id, formData);
      setLogoUrl(response.url);

      // Trigger form change detection
      if (onChange) onChange();

      return response;
    } finally {
      setUploadingLogo(false);
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getFormData: async () => {
      try {
        const values = await form.validateFields();
        return {
          values,
          currentAddress
        };
      } catch (error) {
        throw new Error("Please fix validation errors in Company Info tab");
      }
    }
  }));

  return (
    <div className="company-info-tab">
      <Form
        layout="vertical"
        hideRequiredMark
        colon={false}
        form={form}
        onFieldsChange={handleFieldChange}
      >
        <div className="form-content">
          <div className="form-row">
            <Form.Item
              label="Company Name"
              name="clientName"
              rules={[{ required: true, message: "Required" }]}
              className="form-item-half"
            >
              <Input size="large" placeholder="Company Name" />
            </Form.Item>
            <Form.Item
              label="Custom ID"
              name="customID"
              rules={[
                { max: 8, message: "Maximum 8 characters" },
                {
                  pattern: /^[a-zA-Z0-9]*$/,
                  message: "Only alphanumeric characters allowed"
                }
              ]}
              className="form-item-half"
            >
              <Input size="large" placeholder="Custom ID (optional)" maxLength={8} />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Support Email"
              name="supportEmail"
              rules={[
                { type: "email", message: "Invalid Email" },
                { required: true, message: "Required" }
              ]}
              className="form-item-half"
            >
              <Input size="large" placeholder="Support Email" />
            </Form.Item>
            <Form.Item
              label="Accounting Email"
              name="accountingEmail"
              rules={[
                { type: "email", message: "Invalid Email" },
                { required: true, message: "Required" }
              ]}
              className="form-item-half"
            >
              <Input size="large" placeholder="Accounting Email" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Support Phone"
              name="supportPhone"
              className="form-item-half"
            >
              <PhoneInput country="ca" enableSearch />
            </Form.Item>
            <Form.Item
              label="Market"
              name="market"
              rules={[{ required: true, message: "Required" }]}
              className="form-item-half"
            >
              <Select size="large" placeholder="Please Select">
                {MARKET_OPTIONS.map((market) => (
                  <Select.Option key={market} value={market}>
                    {market}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="Industry" name="industry" className="form-item-half">
              <Select
                size="large"
                placeholder="Please Select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                <Select.Option value="">Please Select</Select.Option>
                {INDUSTRY_OPTIONS.map((industry) => (
                  <Select.Option key={industry} value={industry}>
                    {industry}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Address Field 1"
              name="addressField1"
              className="form-item-half"
            >
              <Input size="large" placeholder="Address Field 1" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Address Field 2"
              name="addressField2"
              className="form-item-half"
            >
              <Input size="large" placeholder="Address Field 2" />
            </Form.Item>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: "Required" }]}
              className="form-item-half"
            >
              <Select
                size="large"
                placeholder="Please Select"
                onChange={onChangeCountry}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes((input ?? "").toLowerCase())
                }
              >
                {loadAllCountries().map((country) => (
                  <Select.Option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="State" name="state" className="form-item-half">
              <Select
                size="large"
                placeholder="Please Select"
                onChange={onChangeState}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes((input ?? "").toLowerCase())
                }
              >
                {[{ name: defaultOptionName, isoCode: "" }, ...countryStates].map(
                  (state) => (
                    <Select.Option key={`state-${state.isoCode}`} value={state.name}>
                      {state.name}
                    </Select.Option>
                  )
                )}
              </Select>
            </Form.Item>
            <Form.Item label="City" name="city" className="form-item-half">
              <Select
                size="large"
                placeholder="Please Select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes((input ?? "").toLowerCase())
                }
              >
                {[{ name: defaultOptionName }, ...stateCities].map((city) => (
                  <Select.Option key={`city-${city.name}`} value={city.name}>
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="Zip Code" name="postalAddress" className="form-item-half">
              <Input size="large" placeholder="Zip Code" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="Company Logo" className="form-item-full">
              <LogoUpload
                logoUrl={logoUrl}
                onUpload={handleLogoUpload}
                uploading={uploadingLogo}
                style={{ marginTop: 8 }}
              />
            </Form.Item>
          </div>

          <Form.Item
            label="Map Address"
            name="address"
            rules={[{ required: true, message: "Required" }]}
          >
            <AddressInput
              placeholder="Address"
              onSelect={(value) => handleSetAddress(value)}
              address={currentAddress}
              onChange={(value) => {
                // Handle direct input changes
                if (typeof value === "string") {
                  form.setFieldsValue({ address: value });
                  if (onChange) onChange();
                }
              }}
            />
          </Form.Item>
          <div className="map">
            <DefaultMapbox
              address={currentAddress}
              setAddress={(value) => {
                handleSetAddress(value);
                // Ensure map clicks trigger change detection
                if (onChange) onChange();
              }}
            />
          </div>

          <Form.Item label="Dedicated Drivers" name="driverIds">
            <Select
              mode="multiple"
              placeholder="Please Select Drivers"
              allowClear
              showSearch
              optionFilterProp="children"
              onSearch={(input) => {
                debouncedFilterDriver(input);
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {driversList?.map((driverList) => {
                if (driverList.active === true)
                  return (
                    <Option key={driverList.id} value={driverList.id}>
                      {fullName(driverList)}
                    </Option>
                  );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Choose which Services will be specific values"
            placeholder="Services"
            name="services"
          >
            <Select
              showSearch
              suffixIcon={<ArrowDownIcon />}
              mode="multiple"
              allowClear
              options={servicesOptions}
              placeholder="Choose a service"
            />
          </Form.Item>

          <Form.Item
            label="Default Route Visibility"
            name="defaultRouteVisibility"
            tooltip="Sets the default visibility for new routes created by this client"
          >
            <Select
              suffixIcon={<ArrowDownIcon />}
              allowClear
              placeholder="Select default route visibility"
              onChange={handleFieldChange}
            >
              <Option value="all">All Drivers</Option>
              <Option value="internal_only">Internal Drivers Only</Option>
              <Option value="manual_only">Manual Assign Only</Option>
            </Select>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
});

CompanyInfoTab.displayName = "CompanyInfoTab";
export default CompanyInfoTab;
